import type { StartNodeMetadata } from "@/types/workflows";

/**
 * The loosest shape every caller shares. Not `OmegaGraph` or `Workflow["graph"]`:
 * the cascade-delete path reads a graph off an untyped `getWorkflow` response,
 * and the AI-edit path holds `OmegaGraph`, whose node union differs. Narrowing
 * happens here once instead of at each call site.
 */
type GraphLike = { nodes?: unknown } | null | undefined;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The Start node's raw metadata bag, or undefined when there is no Start node
 * or it carries none.
 */
export function getStartNodeMetadata(
  graph: GraphLike
): Record<string, unknown> | undefined {
  const nodes = graph?.nodes;
  if (!Array.isArray(nodes)) return undefined;

  const start = nodes.find((node) => isRecord(node) && node.type === "start");
  if (!isRecord(start)) return undefined;

  return isRecord(start.metadata) ? start.metadata : undefined;
}

/**
 * The two tool fields the publish path reads off a Start node.
 *
 * `exposeAsTool` is `null`, not `false`, when the node carries no tool metadata
 * at all, so callers can tell "not a tool" apart from "nothing said". Mirrors
 * `startTransformer.fromOmega`, so both sides of the round-trip agree.
 */
export function getStartToolMetadata(graph: GraphLike): {
  toolId: string | null;
  exposeAsTool: boolean | null;
} {
  const metadata = getStartNodeMetadata(graph) as StartNodeMetadata | undefined;

  const toolId =
    typeof metadata?.toolId === "string" && metadata.toolId.length > 0
      ? metadata.toolId
      : null;

  if (!metadata) return { toolId, exposeAsTool: null };
  if (typeof metadata.exposeAsTool === "boolean") {
    return { toolId, exposeAsTool: metadata.exposeAsTool };
  }
  return { toolId, exposeAsTool: toolId ? true : null };
}
